/**
 * Flight Status Mapper Utility
 * 
 * Maps flight hop status messages to user-friendly labels for display based on the status indicator.
 */

export interface StatusMapping {
  label: string;
  color: string;
}

export class FlightStatusMapper {
  private static readonly STATUS_MAPPINGS: Record<string, StatusMapping> = {
    'CANCELLED': { label: 'CANCELLED', color: 'Red' },
    'DIVERTED': { label: 'DIVERTED', color: 'Red' },
    'REDIRECTED': { label: 'REDIRECTED', color: 'Red' },
    'Scheduled On-time': { label: 'SCHEDULED ON TIME', color: 'Green' },
    'En Route, Departed On-time': { label: 'DEPARTED ON TIME', color: 'Green' },
    'En Route, Arriving On-time': { label: 'ARRIVING ON TIME', color: 'Green' },
    'Inconsistent Information': { label: 'STATUS UNAVAILABLE', color: 'Gray' },
    'Arrived On-time': { label: 'ARRIVED ON TIME', color: 'Green' },
    'Booked': { label: 'STATUS UNAVAILABLE', color: 'Gray' },
    'Unknown': { label: 'STATUS UNAVAILABLE', color: 'Gray' }, // Added for hop.js compatibility
    'UNKNOWN': { label: 'STATUS UNAVAILABLE', color: 'Gray' },
    'Cancelled': { label: 'CANCELLED', color: 'Red' },
    'Diverted': { label: 'DIVERTED', color: 'Red' },
    'Redirected': { label: 'REDIRECTED', color: 'Red' },
  };

  private static readonly STATUS_PATTERNS: Array<{ pattern: RegExp; label: string; color: string; extractTime?: boolean }> = [
    { pattern: /^Departure\s+Delayed\s+(.+)$/i, label: 'DEPARTURE DELAYED', color: 'Red', extractTime: true },
    { pattern: /^En\s+Route,\s+Departure\s+Delayed\s+(.+)$/i, label: 'DEPARTED DELAYED', color: 'Red', extractTime: true },
    { pattern: /^En\s+Route,\s+Arrival\s+Delayed\s+(.+)$/i, label: 'ARRIVAL DELAYED', color: 'Red', extractTime: true },
    { pattern: /^Arrived\s+Delayed\s+(.+)$/i, label: 'ARRIVED DELAYED', color: 'Red', extractTime: true },
  ];

  /**
   * Maps a flight status message to a display label using the status indicator
   */
  public static mapStatus(statusMessage: string, statusIndicator?: string): StatusMapping {
    if (!statusMessage) {
      return { label: 'STATUS UNAVAILABLE', color: 'Gray' };
    }

    // Check direct mappings first
    if (this.STATUS_MAPPINGS[statusMessage]) {
      const mapping = this.STATUS_MAPPINGS[statusMessage];
      // If the label is STATUS UNAVAILABLE, always use Gray color
      if (mapping.label === 'STATUS UNAVAILABLE') {
        return { label: mapping.label, color: 'Gray' };
      }
      return mapping;
    }

    // Check pattern-based mappings for delayed flights
    for (const pattern of this.STATUS_PATTERNS) {
      const match = statusMessage.match(pattern.pattern);
      if (match) {
        const timeString = pattern.extractTime && match[1] ? ` ${this.formatTimeString(match[1].trim())}` : '';
        return {
          label: `${pattern.label}${timeString}`,
          color: pattern.color
        };
      }
    }

    // Fallback to STATUS UNAVAILABLE with Gray color
    return {
      label: 'STATUS UNAVAILABLE',
      color: 'Gray'
    };
  }

  /**
   * Formats time strings from status messages to be more concise
   */
  private static formatTimeString(timeStr: string): string {
    // Convert common time formats to shorter versions
    return timeStr
      .replace(/(\d+)H\s*(\d+)MINS?/i, '$1H$2M')  // "1H 15MINS" -> "1H15M"
      .replace(/(\d+)\s*MINS?/i, '$1M')           // "30MINS" or "30mins" -> "30M"
      .replace(/(\d+)\s*HOURS?/i, '$1H')          // "2HOURS" -> "2H"
      .toUpperCase();
  }

  /**
   * Gets status mapping directly from hop data using status indicator and message
   * This is the preferred method for getting status from hop data
   */
  public static getHopStatus(hop: { statusMessage?: string; statusIndicator?: string }): StatusMapping {
    return this.mapStatus(hop.statusMessage || '', hop.statusIndicator);
  }
}